import { RewindData, RewindValue } from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import moment from 'moment-timezone';
import { useCallback, useState } from 'react';
import { DeepNonNullable, DeepNullable, DeepRequired } from 'ts-essentials';

import { analyticsApi } from 'apis/analytics';
import { rewindConfig } from 'components/rewind';

// ============================================================================
// 🎭 DEMO MODE - Uncomment ONE line below to use mock data instead of API
// ============================================================================
// import { MOCK_STAR_PERFORMER as MOCK_DATA } from './utils/mockRewindData';
// import { MOCK_SINGLE_CHANNEL as MOCK_DATA } from './utils/mockRewindData';
// import { MOCK_NO_DATA as MOCK_DATA } from './utils/mockRewindData';
// import { MOCK_RANDOM_CSAT as MOCK_DATA } from './utils/mockRewindData';
// import { MOCK_NO_CSAT as MOCK_DATA } from './utils/mockRewindData';
// ============================================================================

export type RewindDataNormalized = DeepRequired<DeepNonNullable<RewindData>>;

type UseRewindDataReturn = {
  rewindData: RewindDataNormalized | null;
  isLoading: boolean;
  isError: boolean;
  fetchRewindData: (userId: string) => Promise<void>;
};

/** Normalizes monthly conversation counts to fill in missing months with zeroes. */
function normalizeMonthlyConversationCounts(
  monthlyConversationCounts: RewindData['monthly_conversation_counts'],
): DeepRequired<
  DeepNonNullable<RewindData['monthly_conversation_counts']>[number]
>[] {
  const currentYear = new Date().getFullYear();
  const result: ReturnType<typeof normalizeMonthlyConversationCounts> = [];
  const safeMonthlyCounts = monthlyConversationCounts ?? [];

  for (let month = 1; month <= 12; month++) {
    const existing = safeMonthlyCounts.find(
      (m) => m.year === currentYear && m.month === month,
    );
    result.push({
      year: currentYear,
      month,
      count: existing?.count ?? 0,
    });
  }

  return result;
}

/** Normalizes channel breakdown to include percentages sorted descending. */
function normalizeChannelBreakdown(
  channelBreakdown: RewindData['channel_breakdown'],
): DeepRequired<DeepNonNullable<RewindData['channel_breakdown']>[number]>[] {
  const safeChannelBreakdown: RewindData['channel_breakdown'] =
    channelBreakdown ?? [];

  const totalCount = safeChannelBreakdown.reduce(
    (sum, item) => sum + (item.count ?? 0),
    0,
  );

  return safeChannelBreakdown
    .map((item) => {
      const count = item.count ?? 0;

      return {
        channel: item.channel ?? 'Unknown',
        media_type: item.media_type ?? 'Unknown',
        count,
        percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0,
      };
    })
    .sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0));
}

/** Normalizes a conversation with fallbacks. */
function normalizeConversation(
  conversation: RewindData['first_conversation'],
): DeepRequired<DeepNonNullable<RewindData['first_conversation']>> {
  if (!conversation) {
    return {
      timestamp: '',
      channel: 'Unknown',
      media_type: 'Unknown',
    };
  }

  return {
    timestamp: conversation.timestamp ?? '',
    channel: conversation.channel ?? 'Unknown',
    media_type: conversation.media_type ?? 'Unknown',
  };
}

/** Normalizes an agent/org value including percentage. */
function normalizeValue(
  value?: DeepNullable<RewindValue>,
  roundDecimals = 0,
): DeepRequired<DeepNonNullable<RewindValue>> {
  const agentValue = value?.agent ?? 0;
  const orgValue = value?.org ?? 0;

  const roundedAgent =
    roundDecimals > 0
      ? Math.round(agentValue * Math.pow(10, roundDecimals)) /
        Math.pow(10, roundDecimals)
      : agentValue;
  const roundedOrg =
    roundDecimals > 0
      ? Math.round(orgValue * Math.pow(10, roundDecimals)) /
        Math.pow(10, roundDecimals)
      : orgValue;

  const percentageOfOrg =
    orgValue > 0 ? Math.round((agentValue / orgValue) * 100) : 0;

  return {
    agent: roundedAgent,
    org: roundedOrg,
    percentage: percentageOfOrg,
  };
}

/** Normalizes the API response data. */
function normalizeApiData(data: RewindData): RewindDataNormalized {
  const d = data ?? {};

  return {
    // Conversation stats
    total_conversations_handled: normalizeValue(d.total_conversations_handled),
    first_conversation: normalizeConversation(d.first_conversation),
    last_conversation: normalizeConversation(d.last_conversation),
    average_messages_per_conversation: normalizeValue(
      d.average_messages_per_conversation,
      1,
    ),
    monthly_conversation_counts: normalizeMonthlyConversationCounts(
      d.monthly_conversation_counts,
    ),

    // Customer & message stats
    unique_customers_helped: normalizeValue(d.unique_customers_helped),
    total_messages_sent: normalizeValue(d.total_messages_sent),
    total_time_spent_helping_ms: normalizeValue(d.total_time_spent_helping_ms),

    // Channel & activity stats
    channel_breakdown: normalizeChannelBreakdown(d.channel_breakdown),
    most_active_hour: d.most_active_hour ?? 0,
    most_active_day_of_week: d.most_active_day_of_week ?? 1,
    busiest_month: {
      year: d.busiest_month?.year ?? new Date().getFullYear(),
      month: d.busiest_month?.month ?? 1,
      count: d.busiest_month?.count ?? 0,
    },
    total_active_days: normalizeValue(d.total_active_days),

    // CSAT stats
    csat_score: normalizeValue(d.csat_score),
    total_ratings_received: normalizeValue(d.total_ratings_received),
    five_star_ratings_count: normalizeValue(d.five_star_ratings_count),
    monthly_csat_scores: [],
  };
}

/** Hook to fetch and transform rewind data for a user. */
export function useRewindData(): UseRewindDataReturn {
  const [rewindData, setRewindData] = useState<RewindDataNormalized | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [readRewindData] = analyticsApi.useLazyReadRewindDataQuery();

  const fetchRewindData = useCallback(
    async (userId: string) => {
      if (!userId) {
        return;
      }

      // 🎭 DEMO MODE: If MOCK_DATA is imported, use it instead of fetching
      // @ts-expect-error -- IGNORE --
      if (typeof MOCK_DATA !== 'undefined') {
        setIsLoading(true);
        // Simulate network delay for realistic demo
        await new Promise((resolve) => setTimeout(resolve, 800));

        // @ts-expect-error -- IGNORE --
        setRewindData(normalizeApiData(MOCK_DATA));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const timezone = moment.tz.guess();
        const currentYear = new Date().getFullYear();

        const response = await readRewindData({
          context: {
            field: 'agent_id',
            operator: 'is_one',
            values: [{ value: userId }],
          },
          period: {
            field: 'created_at',
            values: [
              {
                value:
                  currentYear === rewindConfig.year ? 'this_year' : 'last_year',
              },
            ],
          },
          filters: [],
          settings: {
            timezone,
          },
        });

        if (isResponseWithData(response)) {
          const normalizedData = normalizeApiData(response.data.data);
          setRewindData(normalizedData);
        } else {
          setIsError(true);
        }
      } catch (error) {
        console.error('Failed to fetch rewind data:', error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [readRewindData],
  );

  return {
    rewindData,
    isLoading,
    isError,
    fetchRewindData,
  };
}
